import { Card, Row, Col, Statistic, Typography, Tooltip } from 'antd';
import { CodeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const GitActivityChart = ({ gitActivity = [] }) => {
  // 生成示例数据（如果没有真实数据）
  const generateSampleData = () => {
    const sampleData = [];
    const today = dayjs();
    
    // 生成过去365天的数据
    for (let i = 364; i >= 0; i--) {
      const date = today.subtract(i, 'days');
      const commits = Math.random() > 0.7 ? Math.floor(Math.random() * 10) + 1 : 0;
      sampleData.push({
        date: date.format('YYYY-MM-DD'),
        commits: commits
      });
    }
    return sampleData;
  };
  
  // 使用传入的数据或生成示例数据
  const activityData = gitActivity.length > 0 ? gitActivity : generateSampleData();
  
  // Git活动图渲染 - GitHub风格贡献图
  const renderGitActivity = () => {
    const today = dayjs();
    
    // 计算开始日期：从今天往前推52周（364天）
    const weeksToShow = 53; // 显示53周以确保覆盖完整的52周
    const daysToShow = weeksToShow * 7;
    const startDate = today.subtract(daysToShow - 1, 'days');
    
    // 找到开始日期所在周的周日
    const startDayOfWeek = startDate.day(); // 0=周日, 1=周一, ..., 6=周六
    const adjustedStartDate = startDate.subtract(startDayOfWeek, 'days');
    
    // 生成周数据
    const weeks = [];
    for (let weekIndex = 0; weekIndex < weeksToShow; weekIndex++) {
      const weekData = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const currentDate = adjustedStartDate.add(weekIndex * 7 + dayIndex, 'days');
        
        // 只显示不超过今天的日期
        if (currentDate.isBefore(today) || currentDate.isSame(today, 'day')) {
          const dayData = activityData.find(item => item.date === currentDate.format('YYYY-MM-DD'));
          weekData.push(dayData || { 
            date: currentDate.format('YYYY-MM-DD'), 
            commits: 0 
          });
        } else {
          // 超出今天的日期用空占位
          weekData.push(null);
        }
      }
      weeks.push(weekData);
    }

    const getContributionColor = (commits) => {
      if (commits === 0) return '#ebedf0';
      if (commits <= 2) return '#c6e48b';
      if (commits <= 4) return '#7bc96f';
      if (commits <= 6) return '#239a3b';
      return '#196127';
    };

    // 生成月份标签 - GitHub风格
    const monthLabels = [];
    let lastMonth = -1;
    
    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week.find(day => day !== null);
      if (firstDayOfWeek) {
        const weekDate = dayjs(firstDayOfWeek.date);
        const currentMonth = weekDate.month();
        
        // 当月份发生变化时添加标签
        if (currentMonth !== lastMonth && weekIndex > 0) {
          monthLabels.push({
            week: weekIndex,
            label: weekDate.format('MMM')
          });
        }
        lastMonth = currentMonth;
      }
    });
    
    // 添加第一个月的标签
    if (weeks[0] && weeks[0].find(day => day !== null)) {
      const firstWeekDate = dayjs(weeks[0].find(day => day !== null).date);
      monthLabels.unshift({
        week: 0,
        label: firstWeekDate.format('MMM')
      });
    }

    return (
      <div className="contribution-graph w-full">
        {/* 月份标签 */}
        <div className="flex mb-2 relative h-4">
          {monthLabels.map((month, index) => (
            <div 
              key={index} 
              className="text-xs text-gray-500 absolute"
              style={{ 
                left: `${(month.week / weeks.length) * 100}%`,
                transform: 'translateX(-50%)'
              }}
            >
              {month.label}
            </div>
          ))}
        </div>
        
        {/* 星期标签和贡献图 */}
        <div className="flex w-full overflow-x-auto">
          {/* 星期标签 */}
          <div className="flex flex-col mr-2 flex-shrink-0" style={{ width: '20px' }}>
            {['', 'M', '', 'W', '', 'F', ''].map((day, index) => (
              <div key={index} className="text-xs text-gray-500 leading-3 mb-1 text-center" style={{ height: '11px' }}>
                {day}
              </div>
            ))}
          </div>
          
          {/* 贡献方块 */}
          <div className="flex gap-1" style={{ minWidth: `${weeks.length * 12}px` }}>
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1" style={{ width: '10px' }}>
                {week.map((day, dayIndex) => (
                  day ? (
                    <Tooltip 
                      key={`${weekIndex}-${dayIndex}`} 
                      title={`${day.commits} contributions on ${dayjs(day.date).format('MMM DD, YYYY')}`}
                    >
                      <div
                        className="rounded-sm cursor-pointer transition-all hover:ring-1 hover:ring-gray-300 mb-1"
                        style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: getContributionColor(day.commits)
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <div
                      key={`${weekIndex}-${dayIndex}`}
                      className="mb-1"
                      style={{ 
                        width: '10px',
                        height: '10px',
                        backgroundColor: 'transparent' 
                      }}
                    />
                  )
                ))}
              </div>
            ))}
          </div>
        </div>
        
        {/* 图例 */}
        <div className="flex items-center justify-end mt-3 text-xs text-gray-500">
          <span className="mr-2">Less</span>
          <div className="flex gap-1">
            {[0, 1, 3, 5, 7].map((level, index) => (
              <div
                key={index}
                className="rounded-sm"
                style={{ 
                  width: '10px',
                  height: '10px',
                  backgroundColor: getContributionColor(level) 
                }}
              />
            ))}
          </div>
          <span className="ml-2">More</span>
        </div>
      </div>
    );
  };

  return (
    <Card title="代码提交活动" extra={<CodeOutlined />}>
      <div className="mb-4">
        <Text strong>最近52周提交情况</Text>
        <div className="mt-4">
          {renderGitActivity()}
        </div>
      </div>
      <Row gutter={16}>
        <Col span={8}>
          <Statistic
            title="总提交数"
            value={activityData.reduce((sum, item) => sum + item.commits, 0)}
            suffix="次"
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="最长连击"
            value={(() => {
              let maxStreak = 0;
              let currentStreak = 0;
              activityData.forEach(item => {
                if (item.commits > 0) {
                  currentStreak++;
                  maxStreak = Math.max(maxStreak, currentStreak);
                } else {
                  currentStreak = 0;
                }
              });
              return maxStreak;
            })()}
            suffix="天"
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="最高单日"
            value={Math.max(...activityData.map(item => item.commits), 0)}
            suffix="次"
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default GitActivityChart;